import { useState, useEffect } from 'react'
import { FiRefreshCw, FiPrinter, FiX, FiTag, FiShoppingCart, FiUsers } from 'react-icons/fi'
import toast from 'react-hot-toast'
import api from '../utils/api'
import { useAuth } from '../App'
import AlberoDetayModal from '../components/AlberoDetayModal'

const fmt = (v) => (v == null || isNaN(v)) ? '0,00' : Number(v).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const fmtNum = (v) => (v == null || isNaN(v)) ? '0' : Number(v).toLocaleString('tr-TR', { maximumFractionDigits: 0 })

const PERIODS = [
  { key: 'daily', label: 'Bugün' },
  { key: 'weekly', label: 'Bu Hafta' },
  { key: 'monthly', label: 'Bu Ay' },
  { key: 'yearly', label: 'Bu Yıl' },
]

export default function AlberoOzet() {
  const { selectedCompanyId } = useAuth()
  const [period, setPeriod] = useState('daily')
  const [grup, setGrup] = useState(null)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [modal, setModal] = useState(null)

  useEffect(() => { load(period, grup) }, [selectedCompanyId])

  const load = async (p, g, refresh = false) => {
    setLoading(true)
    try {
      const { data: r } = await api.get('/albero/ozet', { params: { period: p, grup: g || undefined, refresh: refresh || undefined } })
      setData(r)
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Özet yüklenemedi')
    }
    setLoading(false)
  }

  const changePeriod = (p) => { setPeriod(p); setGrup(null); load(p, null) }
  const selectGrup = (g) => { const ng = grup === g ? null : g; setGrup(ng); load(period, ng) }

  const k = data?.kpis || {}
  const kpiCards = [
    { label: 'CİRO (KDV DAHİL)', value: fmt(k.ciro), suffix: 'TL', color: 'text-blue-700 dark:text-blue-400', b: 'border-blue-400' },
    { label: 'NET SATIŞ', value: fmt(k.net_satis), suffix: 'TL', color: 'text-green-700 dark:text-green-400', b: 'border-green-400' },
    { label: 'İADE', value: fmt(k.iade), suffix: 'TL', color: 'text-red-600 dark:text-red-400', b: 'border-red-400' },
    { label: 'MİKTAR', value: fmtNum(k.miktar), suffix: 'adet', color: 'text-purple-700 dark:text-purple-400', b: 'border-purple-400' },
    { label: 'FATURA', value: fmtNum(k.fatura_sayisi), suffix: 'adet', color: 'text-orange-700 dark:text-orange-400', b: 'border-orange-400' },
    { label: 'CARİ SAYISI', value: fmtNum(k.cari_sayisi), suffix: 'cari', color: 'text-teal-700 dark:text-teal-400', b: 'border-teal-400' },
  ]

  const periodLabel = PERIODS.find(p => p.key === period)?.label || ''

  return (
    <div className="p-4 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-700 rounded-xl p-4 mb-4 text-white no-print">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h1 className="text-lg font-bold">Albero — Anlık Satış Raporu</h1>
          <div className="flex items-center gap-2">
            <button onClick={() => load(period, grup, true)} disabled={loading}
              className="flex items-center gap-1 bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded text-xs">
              <FiRefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Yenile
            </button>
            <button onClick={() => window.print()}
              className="flex items-center gap-1 bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded text-xs">
              <FiPrinter size={12} /> Yazdır
            </button>
          </div>
        </div>
        {/* Dönem butonları */}
        <div className="flex items-center gap-1.5 mt-3">
          {PERIODS.map(p => (
            <button key={p.key} onClick={() => changePeriod(p.key)}
              className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-colors ${period === p.key ? 'bg-white text-slate-800' : 'bg-white/10 hover:bg-white/20 text-white'}`}>
              {p.label}
            </button>
          ))}
          <span className="text-xs text-white/60 ml-2">
            {data && `${data.start_date} → ${data.end_date}`}
          </span>
        </div>
      </div>

      {/* Yazdırma başlığı (sadece print) */}
      <div className="hidden print:block mb-3">
        <h1 className="text-xl font-bold">Albero — Anlık Satış Raporu</h1>
        <div className="text-sm text-gray-600">{periodLabel} · {data?.start_date} → {data?.end_date}{grup ? ` · Grup: ${grup}` : ''}</div>
      </div>

      {/* KPI'lar */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
          {kpiCards.map((c, i) => (
            <div key={i} className={`bg-white dark:bg-gray-800 rounded-lg border-t-[3px] ${c.b} p-3 shadow-sm`}>
              <div className="text-[9px] font-semibold text-gray-400 uppercase mb-1">{c.label}</div>
              <div className={`text-base font-bold ${c.color}`}>{c.value}</div>
              <div className="text-[9px] text-gray-400">{c.suffix}</div>
            </div>
          ))}
        </div>
      )}

      {grup && (
        <div className="mb-3 flex items-center gap-2 text-xs no-print">
          <span className="text-gray-500">Grup filtresi:</span>
          <span className="inline-flex items-center gap-1 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-2 py-1 rounded">
            {grup} <button onClick={() => selectGrup(grup)}><FiX size={12} /></button>
          </span>
        </div>
      )}

      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Gruplar */}
          <Panel icon={FiTag} title="Gruplara Göre Satış" count={data.gruplar?.length}>
            <table className="w-full text-xs">
              <Thead cols={['Grup', 'Ciro', 'Miktar', 'Cari']} />
              <tbody>
                {(data.gruplar || []).map((g, i) => (
                  <tr key={i} onClick={() => selectGrup(g.GRUP)}
                    className={`border-b dark:border-gray-700/50 cursor-pointer ${grup === g.GRUP ? 'bg-blue-50 dark:bg-blue-900/30' : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'}`}>
                    <td className="px-2 py-1.5 font-medium">{g.GRUP}</td>
                    <td className="text-right px-2 py-1.5 text-blue-700 dark:text-blue-400 font-medium">{fmt(g.CIRO)}</td>
                    <td className="text-right px-2 py-1.5">{fmtNum(g.MIKTAR)}</td>
                    <td className="text-right px-2 py-1.5 text-gray-500">{g.CARI_SAY}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>

          {/* Cariler */}
          <Panel icon={FiUsers} title="Hangi Carilere Satıldı" count={data.cariler?.length}>
            <table className="w-full text-xs">
              <Thead cols={['Cari', 'Ciro', 'Miktar', 'Fat.']} />
              <tbody>
                {(data.cariler || []).map((c, i) => (
                  <tr key={i} onClick={() => setModal({ type: 'cari', blcari: c.BLCARI, unvan: c.CARI })}
                    className="border-b dark:border-gray-700/50 cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20">
                    <td className="px-2 py-1.5 font-medium text-blue-600 dark:text-blue-400 truncate max-w-[220px]">{c.CARI}</td>
                    <td className="text-right px-2 py-1.5 font-medium">{fmt(c.CIRO)}</td>
                    <td className="text-right px-2 py-1.5">{fmtNum(c.MIKTAR)}</td>
                    <td className="text-right px-2 py-1.5 text-gray-500">{c.FAT_SAY}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>

          {/* Ürünler */}
          <Panel icon={FiShoppingCart} title={`Ürünlere Göre Satış${grup ? ` — ${grup}` : ''}`} count={data.urunler?.length} wide>
            <table className="w-full text-xs">
              <Thead cols={['Ürün', 'Grup', 'Miktar', 'Ciro']} />
              <tbody>
                {(data.urunler || []).map((u, i) => (
                  <tr key={i} className="border-b dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                    <td className="px-2 py-1.5">{u.URUN}</td>
                    <td className="px-2 py-1.5 text-gray-500">{u.GRUP}</td>
                    <td className="text-right px-2 py-1.5">{fmtNum(u.MIKTAR)}</td>
                    <td className="text-right px-2 py-1.5 text-blue-700 dark:text-blue-400 font-medium">{fmt(u.CIRO)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
        </div>
      )}

      {!data && !loading && <div className="text-center text-gray-400 py-20">Yükleniyor...</div>}
      {modal && <AlberoDetayModal initial={modal} onClose={() => setModal(null)} />}
    </div>
  )
}

function Panel({ icon: Icon, title, count, children, wide }) {
  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border dark:border-gray-700 overflow-hidden ${wide ? 'lg:col-span-2' : ''} print:break-inside-avoid`}>
      <div className="flex items-center gap-2 px-3 py-2 bg-slate-700 text-white">
        <Icon size={14} />
        <span className="text-sm font-medium flex-1">{title}</span>
        {count != null && <span className="text-[10px] bg-white/20 rounded px-1.5 py-0.5">{count}</span>}
      </div>
      <div className="overflow-auto" style={{ maxHeight: '420px', overscrollBehavior: 'contain' }}>
        {children}
      </div>
    </div>
  )
}

function Thead({ cols }) {
  return (
    <thead className="sticky top-0 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-200 text-[10px] uppercase">
      <tr>
        {cols.map((c, i) => (
          <th key={i} className={`px-2 py-1.5 font-medium ${i === 0 ? 'text-left' : 'text-right'}`}>{c}</th>
        ))}
      </tr>
    </thead>
  )
}
